import {Component, ViewChild, Input, Output, EventEmitter} from '@angular/core';
import {RecipeImageManagerComponent} from '../../components/recipe-image-manager/recipe-image-manager.component';
import {UiModalComponent} from '../../shared/ui';
import {AppLogger, AppLoggerService} from '../../core/logging';

/**
 * Hosts the full recipe image manager in a modal, opened from the recipe detail
 * page's hero image. Mirrors {@link ProductImageDialogComponent} so recipes and
 * products share the same "tap the image to manage it" pattern (and the iOS
 * handling).
 */
@Component({
  selector: 'app-recipe-image-dialog',
  imports: [
    RecipeImageManagerComponent,
    UiModalComponent
  ],
  templateUrl: './recipe-image-dialog.component.html'
})
export class RecipeImageDialogComponent {

  private readonly log: AppLogger;

  @Input() recipeId: number | null = null;

  /** Source URL of an imported recipe; enables "refetch images from source". */
  @Input() sourceUrl = '';

  @Output() modalClosed = new EventEmitter<void>();

  @ViewChild(UiModalComponent) modal!: UiModalComponent;
  @ViewChild(RecipeImageManagerComponent) manager?: RecipeImageManagerComponent;

  constructor(private appLogger: AppLoggerService) {
    this.log = this.appLogger.createForClass(RecipeImageDialogComponent);
  }

  get hasRecipeId(): boolean {
    return this.recipeId !== null;
  }

  /**
   * Open the dialog. `startInSearch` lands directly on the image-search panel,
   * which is what the detail page's "Search image" action wants — the modal
   * body is always projected, so the manager exists before the dialog is shown.
   */
  open(startInSearch = false): void {
    this.modal.open();
    if (startInSearch) {
      this.manager?.openSearch();
    } else {
      this.manager?.closeSearch();
    }
  }

  close(): void {
    // Called FROM the modal's (modalClosed) event — the modal is already
    // closing, so don't call this.modal.close() here.
    this.log.debug('Recipe image dialog emitting modalClosed event');
    this.modalClosed.emit();
  }
}
